import {
  InstallmentApplication,
  InstallmentStatus,
} from "./InstallmentApplication";

export type CreditStatus = "PENDING" | "APPROVED" | "REJECTED" | "FINISHED";

export enum DocumentTypeEnum {
  CC = "CC",
  CE = "CE",
  PAS = "PAS",
  NIT = "NIT",
  TI = "TI",
}

export interface CreditApplication {
  id?: string;
  applicantName: string;
  document: string;
  documentType: DocumentTypeEnum;
  amount: number;
  termMonths: number;
  purpose: string;
  emailOptional?: string | null;
  phoneOptional?: string | null;
  guarantorPhone: string;
  guarantorId?: string;
  guarantorDocId?: string;
  guarantorName: string;
  accountBank: string;
  accountType: string;
  accountNumber: string;
  isConsentAvailable: boolean;
  consentPdfUploaded: string;
  status: CreditStatus;
  interestRate: number;
  paymentType?: "CAPITAL_INTEREST" | "INTEREST_ONLY";
  moraAnulada?: boolean;
  approvalDate: string;
  paymentDate: string;
  createdAt: string;
  updatedAt: string;
  installments?: InstallmentApplication[];
  qualification?: number;
}

export class ApplicationRequest {
  app: Partial<CreditApplication>;

  constructor({ app }: { app: Partial<CreditApplication> }) {
    this.app = app;
  }
}

export class ApplicationAdminRequest {
  app: Partial<CreditApplication>;
  targetUserId: string;

  constructor({ app, targetUserId }: { app: Partial<CreditApplication>; targetUserId: string }) {
    this.app = app;
    this.targetUserId = targetUserId;
  }
}

export class InstallmentRequest {
  applicationId: string;
  installmentId: string;
  url: string;
  installmentStatus: InstallmentStatus;

  constructor({
    applicationId,
    installmentId,
    url,
    installmentStatus,
  }: {
    applicationId: string;
    installmentId: string;
    url: string;
    installmentStatus: InstallmentStatus;
  }) {
    this.applicationId = applicationId;
    this.installmentId = installmentId;
    this.url = url;
    this.installmentStatus = installmentStatus;
  }
}

export class UpdateInstallmentRequest {
  applicationId: string;
  installmentId: string;
  interestAmount: number;
  capitalAmount: number;
  isPaymentInstallment: boolean;
  rejectReason: string | undefined;
  isRejected: boolean;

  constructor({
    applicationId,
    installmentId,
    interestAmount,
    capitalAmount,
    isPaymentInstallment,
    rejectReason,
    isRejected,
  }: {
    applicationId: string;
    installmentId: string;
    interestAmount: number;
    capitalAmount: number;
    isPaymentInstallment: boolean;
    rejectReason?: string;
    isRejected: boolean;
  }) {
    this.applicationId = applicationId;
    this.installmentId = installmentId;
    this.interestAmount = interestAmount;
    this.capitalAmount = capitalAmount;
    this.isPaymentInstallment = isPaymentInstallment;
    this.rejectReason = rejectReason;
    this.isRejected = isRejected;
  }
}

export function getApprovedTotals(app: CreditApplication): {
  totalApproved: number;
  balance: number;
} {
  if (!app.installments || app.installments.length === 0) {
    return { totalApproved: 0, balance: app.amount };
  }
  const totalApproved = app.installments
    .filter((inst) => inst.installmentStatus === "APPROVED")
    .reduce((sum, inst) => sum + inst.capitalAmount, 0);
  const balance = app.amount - totalApproved;
  return { totalApproved, balance };
}
